import { useCallback, useEffect, useState } from 'react';
import { type BlockerFunction, useBlocker, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { ApiError } from '../../services/api.js';
import { HistoryTab } from './HistoryTab.js';
import { AutoWorkflowCard, UnpaidBookingsCard } from './cards/AutomationCards.js';
import { BookingEmailsCard } from './cards/BookingEmailsCard.js';
import {
  BookingBufferCard,
  ExtensionsCard,
  InspectionPhotosCard,
  OperatingHoursCard,
} from './cards/BookingRulesCards.js';
import { HomePopupCard } from './cards/HomePopupCard.js';
import { LegalSummaryCard } from './cards/LegalSummaryCard.js';
import { MileagePackagesCard } from './cards/MileagePackagesCard.js';
import { PaymentChannelsCard, PlatformFeeCard, SstCard } from './cards/MoneyCards.js';
import { PenaltyQrCard } from './cards/PenaltyQrCard.js';
import { ReceiptDetailsCard } from './cards/ReceiptDetailsCard.js';
import type { CardProps, FieldRefusal } from './cards/cardProps.js';
import { ConfirmOnDialog, type OnRequest } from './parts/ConfirmOnDialog.js';
import { LeaveDialog, UnsavedBar } from './parts/parts.js';
import { useLatestChanges, usePlatformConfig, useSaveSettings } from './settingsApi.js';
import { type ConfigKey, type Mode, TABS, TAB_KEYS, type TabId, isTabId } from './settingsTypes.js';
import { useSettingsDraft } from './useSettingsDraft.js';

/** Which saved fields each PATCH key validates, to put a refusal on the card it concerns (US3-3). */
const KEY_FIELDS: Record<ConfigKey, string[]> = {
  operating_hours_precedence: ['operating_hours_precedence'],
  platform_fee: ['platform_fee', 'amount_minor'],
  sst: ['sst', 'rate_bps'],
  channel_configs: ['channel_configs'],
  extension_policy: ['min_notice_minutes', 'max_total_extension_minutes'],
  booking_timers: ['hold_minutes', 'unpaid_cancel_mode', 'unpaid_cancel_minutes'],
  customer_inspection_photos: ['customer_inspection_photos'],
  inspection_windows: [
    'pickup_before_minutes',
    'pickup_after_minutes',
    'return_before_minutes',
    'return_after_minutes',
  ],
  auto_workflow: ['auto_workflow'],
  booking_buffer_minutes: ['booking_buffer_minutes'],
  home_popup: ['home_popup'],
  receipt_details: ['receipt_details'],
  booking_emails: ['booking_emails'],
  penalty_payment: ['penalty_payment'],
  mileage_packages: ['mileage_packages'],
};

type SaveError =
  | { kind: 'conflict' }
  | { kind: 'invalid'; message: string; key: ConfigKey | null; refusals?: FieldRefusal[] };

/** 389: a refusal that lists its reasons by field (the home pop-up's). */
function fieldRefusals(err: unknown): FieldRefusal[] | null {
  if (!(err instanceof ApiError)) return null;
  const list = err.details.refusals;
  return Array.isArray(list) ? (list as FieldRefusal[]) : null;
}

function tabOf(params: URLSearchParams): TabId {
  const t = params.get('tab');
  return isTabId(t) ? t : 'money';
}

/**
 * 356: Settings as tabs (design 1b). Money · Booking rules · Automation · Legal · History.
 *
 * - The open tab lives in the address (`?tab=`), so it survives a reload and can be linked (FR-002).
 * - Nothing saves on change: each tab saves all its edits in one request, or none (FR-005, FR-009).
 * - Leaving with edits, by tab, menu, back button or reload, asks first (FR-008).
 * - Moving an automation to On asks first, with what it would act on now (FR-011).
 */
export function SettingsPage() {
  const config = usePlatformConfig();
  const draft = useSettingsDraft(config.data);
  const latest = useLatestChanges(!!config.data);
  const save = useSaveSettings();
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [onRequest, setOnRequest] = useState<OnRequest | null>(null);

  const status = (config.error as { status?: number } | null)?.status;
  const isRefused = status === 403 || status === 401;
  const dirtyCount = draft.dirtyKeys.length;

  // FR-008: any navigation away from these edits (another tab, a menu link, back) is asked first.
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        dirtyCount > 0 &&
        (currentLocation.pathname !== nextLocation.pathname ||
          tabOf(new URLSearchParams(currentLocation.search)) !==
            tabOf(new URLSearchParams(nextLocation.search))),
      [dirtyCount],
    ),
  );

  // FR-008: a reload or a closed browser tab is asked by the browser itself, only while dirty.
  useEffect(() => {
    if (dirtyCount === 0) return;
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirtyCount]);

  function openTab(next: TabId) {
    setSaveError(null);
    setParams({ tab: next }, { replace: true });
  }

  function send(body: Record<string, unknown>, keys: ConfigKey[]) {
    setSaveError(null);
    save.mutate(body, {
      onSuccess: () => {
        draft.reset(keys);
        setOnRequest(null);
      },
      onError: (err) => {
        setOnRequest(null);
        if (err instanceof ApiError && err.status === 409 && err.code === 'SETTINGS_CHANGED') {
          setSaveError({ kind: 'conflict' });
          return;
        }
        const message = err instanceof Error ? err.message : 'Save failed. Try again.';
        const refusals = fieldRefusals(err);
        if (refusals) {
          const named = refusals[0]?.field.split('.')[0] as ConfigKey | undefined;
          const key = named && keys.includes(named) ? named : null;
          setSaveError({ kind: 'invalid', message, key, refusals });
          return;
        }
        const key = keys.find((k) => KEY_FIELDS[k].some((f) => message.includes(f))) ?? null;
        setSaveError({ kind: 'invalid', message, key });
      },
    });
  }

  /** Save the open tab. An automation moving to On is confirmed first (US4). */
  function saveTab() {
    const req = draft.saveRequest(tab);
    if (!req) return;
    const saved = draft.saved;
    const timers = draft.value('booking_timers');
    const workflow = draft.value('auto_workflow');
    const fromTimers: Mode | undefined = saved?.booking_timers?.unpaid_cancel_mode;
    const fromWorkflow: Mode | undefined = saved?.auto_workflow?.mode;
    if (timers && fromTimers && fromTimers !== 'on' && timers.unpaid_cancel_mode === 'on') {
      setOnRequest({ automation: 'unpaid_bookings', from: fromTimers, timers });
      return;
    }
    if (workflow && fromWorkflow && fromWorkflow !== 'on' && workflow.mode === 'on') {
      setOnRequest({ automation: 'auto_workflow', from: fromWorkflow });
      return;
    }
    send(req.body, req.keys);
  }

  /** The dialog's choice: keep the saved mode, save Count only, or save On. */
  function resolveOn(choice: 'keep' | 'count' | 'on') {
    if (!onRequest) return;
    if (choice === 'keep') {
      if (onRequest.automation === 'unpaid_bookings') {
        const timers = draft.value('booking_timers');
        if (timers) draft.set('booking_timers', { ...timers, unpaid_cancel_mode: onRequest.from });
      } else {
        draft.set('auto_workflow', { mode: onRequest.from });
      }
      setOnRequest(null);
      return;
    }
    if (choice === 'count') {
      const req = draft.saveRequest(tab);
      if (!req) return;
      const body = { ...req.body };
      if (onRequest.automation === 'unpaid_bookings') {
        body.booking_timers = { ...(body.booking_timers as object), unpaid_cancel_mode: 'count' };
      } else {
        body.auto_workflow = { mode: 'count' };
      }
      send(body, req.keys);
      return;
    }
    const req = draft.saveRequest(tab);
    if (req) send(req.body, req.keys);
  }

  const cardProps: CardProps | null = config.data
    ? {
        draft,
        config: config.data,
        latest: latest.data,
        refusal: (key) =>
          saveError?.kind === 'invalid' && saveError.key === key ? saveError.message : null,
        refusals: (key) =>
          saveError?.kind === 'invalid' && saveError.key === key ? (saveError.refusals ?? []) : [],
      }
    : null;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Settings"
        description="How the platform behaves for every booking. Each change is recorded in History."
      />

      {config.isLoading && (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}
      {config.isError &&
        (isRefused ? (
          <p className="text-sm text-steel">
            Platform settings are not available for your account.
          </p>
        ) : (
          <p className="text-sm text-red-600">
            Platform settings could not be loaded. Please try again shortly.
          </p>
        ))}

      {cardProps && (
        <>
          <div
            role="tablist"
            aria-label="Settings sections"
            className="-mt-1 mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-hairline"
          >
            {TABS.map((t) => {
              const dirty = draft.dirtyIn(t.id).length > 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => openTab(t.id)}
                  className={[
                    '-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm transition-colors',
                    tab === t.id
                      ? 'border-primary-deep font-semibold text-primary-deep'
                      : 'border-transparent font-medium text-steel hover:text-ink',
                  ].join(' ')}
                >
                  {t.label}
                  {dirty && (
                    <span
                      data-testid={`tab-${t.id}-dot`}
                      aria-label="unsaved changes"
                      className="h-1.5 w-1.5 rounded-full bg-semantic-warning-text"
                    />
                  )}
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            aria-label={TABS.find((t) => t.id === tab)?.label}
            className="flex flex-col gap-4"
          >
            {saveError?.kind === 'conflict' && (
              <div
                role="alert"
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-semantic-danger-text bg-semantic-danger-bg px-4 py-2.5 text-sm text-semantic-danger-text"
              >
                <span>Someone else changed these settings. Reload to see their change.</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSaveError(null);
                    void config.refetch();
                    void latest.refetch();
                  }}
                >
                  Reload
                </Button>
              </div>
            )}
            {saveError?.kind === 'invalid' && saveError.key === null && (
              <p role="alert" className="text-sm text-semantic-danger-text">
                {saveError.message}
              </p>
            )}

            {tab === 'money' && (
              <>
                <PlatformFeeCard {...cardProps} />
                <SstCard {...cardProps} />
                <PaymentChannelsCard {...cardProps} />
                <MileagePackagesCard {...cardProps} />
              </>
            )}
            {tab === 'booking-rules' && (
              <>
                <OperatingHoursCard {...cardProps} />
                <ExtensionsCard {...cardProps} />
                <BookingBufferCard {...cardProps} />
                <InspectionPhotosCard {...cardProps} />
              </>
            )}
            {tab === 'automation' && (
              <>
                <UnpaidBookingsCard {...cardProps} />
                <AutoWorkflowCard {...cardProps} />
              </>
            )}
            {tab === 'legal' && <LegalSummaryCard latest={latest.data} />}
            {tab === 'app' && (
              <>
                <HomePopupCard {...cardProps} />
                <ReceiptDetailsCard {...cardProps} />
                <PenaltyQrCard {...cardProps} />
              </>
            )}
            {tab === 'notifications' && <BookingEmailsCard {...cardProps} />}
            {tab === 'history' && <HistoryTab />}
            {/* #1368: after the tab's cards, pinned to the bottom of the screen while there are unsaved changes, so Save
                is in reach wherever the edit was made on a long tab. */}
            {TAB_KEYS[tab].length > 0 && (
              <UnsavedBar
                count={draft.dirtyIn(tab).length}
                saving={save.isPending}
                onDiscard={() => {
                  draft.reset(TAB_KEYS[tab]);
                  setSaveError(null);
                }}
                onSave={saveTab}
              />
            )}
          </div>

          <LeaveDialog
            open={blocker.state === 'blocked'}
            count={dirtyCount}
            onStay={() => blocker.reset?.()}
            onDiscard={() => {
              draft.reset(draft.dirtyKeys);
              setSaveError(null);
              blocker.proceed?.();
            }}
          />
          <ConfirmOnDialog
            request={onRequest}
            saving={save.isPending}
            onKeep={() => resolveOn('keep')}
            onCountFirst={() => resolveOn('count')}
            onTurnOn={() => resolveOn('on')}
          />
        </>
      )}
    </div>
  );
}
